/**
 * Article guide interactions:
 * - Auto TOC from H2s + continuous progress rail
 * - Explored message at end of TOC
 * - View count (eye)
 * - Local upvote/downvote
 * - Copy link
 */
(function () {
  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80);
  }

  function uniqueId(base, used) {
    let id = base || 'section';
    let i = 2;
    while (used.has(id)) {
      id = base + '-' + i;
      i += 1;
    }
    used.add(id);
    return id;
  }

  function initToc(root) {
    const content = root.querySelector('[data-article-content]');
    const desktopList = root.querySelector('[data-toc-list]');
    const mobileList = root.querySelector('[data-toc-list-mobile]');
    const lists = [desktopList, mobileList].filter(Boolean);
    const explored = root.querySelector('[data-explored-banner]');
    const progressEl = root.querySelector('[data-toc-progress]');
    if (!content || !lists.length) return;

    const headings = Array.from(content.querySelectorAll('h2'));
    if (!headings.length) {
      root.querySelectorAll('.article-guide__toc-wrap, .article-guide__toc-mobile').forEach((el) => {
        el.hidden = true;
      });
      return;
    }

    const used = new Set();
    const items = headings.map((heading) => {
      const existing = heading.id;
      const id = uniqueId(existing || slugify(heading.textContent), used);
      heading.id = id;
      return { id, text: heading.textContent.trim(), el: heading };
    });

    lists.forEach((list) => {
      list.innerHTML = '';
      items.forEach((item) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + item.id;
        a.className = 'article-guide__toc-link';
        a.textContent = item.text;
        a.dataset.tocTarget = item.id;
        li.appendChild(a);
        list.appendChild(li);
      });
    });

    const links = root.querySelectorAll('.article-guide__toc-link');
    let activeIndex = 0;
    let maxReached = 0;

    function setActive(id) {
      const index = items.findIndex((item) => item.id === id);
      if (index < 0) return;
      activeIndex = index;
      maxReached = Math.max(maxReached, index);

      links.forEach((link) => {
        link.classList.toggle('is-active', link.dataset.tocTarget === id);
      });

      updateProgress();
    }

    function updateProgress() {
      if (!progressEl) return;
      const total = Math.max(items.length - 1, 1);
      const ratio = maxReached / total;
      progressEl.style.height = Math.round(ratio * 100) + '%';

      if (explored && maxReached >= items.length - 1) {
        explored.classList.add('is-visible');
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        rootMargin: '-18% 0px -62% 0px',
        threshold: [0, 0.25, 0.5, 1],
      }
    );

    items.forEach((item) => observer.observe(item.el));

    // Full explore when bottom of content is reached
    if (explored) {
      const endObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            maxReached = items.length - 1;
            if (progressEl) progressEl.style.height = '100%';
            explored.classList.add('is-visible');
            if (items.length) {
              links.forEach((link) => {
                link.classList.toggle(
                  'is-active',
                  link.dataset.tocTarget === items[items.length - 1].id
                );
              });
            }
          });
        },
        { threshold: 0.15 }
      );

      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.height = '1px';
      content.appendChild(sentinel);
      endObserver.observe(sentinel);
    }

    // Initialize first item
    if (items[0]) setActive(items[0].id);

    root.querySelectorAll('.article-guide__toc-mobile .article-guide__toc-link').forEach((link) => {
      link.addEventListener('click', () => {
        const details = link.closest('details');
        if (details) details.open = false;
      });
    });
  }

  function initViews(root) {
    const views = root.querySelector('[data-article-views]');
    if (!views) return;

    const articleId = root.dataset.articleId || 'article';
    const countKey = 'article-guide-views:' + articleId;
    const seenKey = 'article-guide-viewed:' + articleId;
    const seed = parseInt(root.dataset.viewSeed || '0', 10) || 0;

    let count = seed;
    try {
      const stored = parseInt(localStorage.getItem(countKey) || '0', 10);
      count = Math.max(seed, stored || 0);
      if (!sessionStorage.getItem(seenKey)) {
        count += 1;
        sessionStorage.setItem(seenKey, '1');
        localStorage.setItem(countKey, String(count));
      } else if (!stored) {
        localStorage.setItem(countKey, String(count));
      }
    } catch (e) {
      count = Math.max(seed, 1);
    }

    const el = views.querySelector('[data-view-count]');
    if (el) el.textContent = String(count);
  }

  function initReactions(root) {
    const groups = root.querySelectorAll('[data-article-reactions]');
    if (!groups.length) return;

    const articleId = root.dataset.articleId || 'article';
    const storageKey = 'article-guide-reaction:' + articleId;
    const countKey = 'article-guide-upvotes:' + articleId;

    let vote = null;
    let count = 0;
    try {
      vote = localStorage.getItem(storageKey);
      count = parseInt(localStorage.getItem(countKey) || '0', 10) || 0;
    } catch (e) {
      /* ignore */
    }

    if (!localStorage.getItem(countKey)) {
      count = Math.max(count, 3 + (Number(articleId) % 40));
      try {
        localStorage.setItem(countKey, String(count));
      } catch (e) {
        /* ignore */
      }
    }

    function render() {
      groups.forEach((group) => {
        group.querySelectorAll('[data-reaction]').forEach((btn) => {
          const type = btn.dataset.reaction;
          const active = vote === type;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        group.querySelectorAll('[data-reaction-count]').forEach((el) => {
          el.textContent = String(count);
        });
      });
    }

    function persist() {
      try {
        if (vote) localStorage.setItem(storageKey, vote);
        else localStorage.removeItem(storageKey);
        localStorage.setItem(countKey, String(count));
      } catch (e) {
        /* ignore */
      }
    }

    groups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-reaction]');
        if (!btn) return;
        const type = btn.dataset.reaction;
        const prev = vote;

        if (prev === type) {
          vote = null;
          if (type === 'up') count = Math.max(0, count - 1);
        } else {
          if (prev === 'up') count = Math.max(0, count - 1);
          vote = type;
          if (type === 'up') count += 1;
        }

        persist();
        render();
      });
    });

    render();
  }

  function initCopy(root) {
    root.querySelectorAll('[data-copy-link]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const url = btn.getAttribute('data-copy-link');
        try {
          await navigator.clipboard.writeText(url);
          btn.setAttribute('aria-label', 'Link copied');
          setTimeout(() => btn.setAttribute('aria-label', 'Copy link'), 1600);
        } catch (e) {
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
      });
    });
  }

  function polishContent(root) {
    const content = root.querySelector('[data-article-content]');
    if (!content) return;

    // Remove default CMS divider lines (we'll add intentional CTA rules instead)
    content.querySelectorAll('hr').forEach((hr) => hr.remove());

    // Remove links from headings (keep the title text only)
    content.querySelectorAll('h1 a, h2 a, h3 a, h4 a, h5 a, h6 a').forEach((link) => {
      link.replaceWith(document.createTextNode(link.textContent));
    });

    // Bold "Explore the full … collection" and drop pointing emoji prefixes
    const boldPatterns = [
      /Explore the full Easter collection/gi,
      /browse the full VikingTreat Easter collection/gi,
      /explore the full VikingTreat Easter collection/gi,
      /Explore the full [^<:]{0,40}? collection/gi,
    ];

    content.querySelectorAll('p, li').forEach((el) => {
      if (el.querySelector('strong.article-guide__cta-label')) return;
      let html = el.innerHTML;
      const before = html;
      html = html.replace(/\s*👉\s*/gu, ' ');
      html = html.replace(/^\s+/, '');

      for (let i = 0; i < boldPatterns.length; i += 1) {
        const pattern = boldPatterns[i];
        if (!pattern.test(html)) continue;
        pattern.lastIndex = 0;
        html = html.replace(pattern, (match) => {
          return '<strong class="article-guide__cta-label">' + match + '</strong>';
        });
        break;
      }

      if (html !== before) el.innerHTML = html;
    });

    // Turn naked URL links + plain-text URLs into CTA buttons
    function ctaLabelFromHref(href) {
      if (/\/collections\//i.test(href)) return 'Browse collection';
      if (/\/products\//i.test(href)) return 'View product';
      if (/\/blogs\//i.test(href)) return 'Read more';
      return 'Shop now';
    }

    function toCtaButton(link, href) {
      const label = ctaLabelFromHref(href);
      link.className = 'article-guide__cta-btn button';
      link.textContent = label;
      link.setAttribute('href', href);
      if (!link.getAttribute('target')) link.setAttribute('target', '_self');
      link.rel = 'noopener';

      const parent = link.parentNode;
      if (parent && parent.matches && parent.matches('p')) {
        const leftover = parent.textContent.replace(label, '').replace(/[👉➡️➜→\s]/gu, '');
        if (!leftover) parent.classList.add('article-guide__cta-btn-wrap');
      }
      return link;
    }

    content.querySelectorAll('a[href]').forEach((link) => {
      if (link.classList.contains('article-guide__cta-btn')) return;
      if (link.closest('h1, h2, h3, h4, h5, h6')) return;

      const href = (link.getAttribute('href') || '').trim();
      const rawText = (link.textContent || '').trim();
      const cleanText = rawText.replace(/^[👉➡️➜→⬅️\s]+/u, '').trim();

      const looksLikeUrlText = /^(https?:\/\/|www\.)/i.test(cleanText);
      const isStorePath = /vikingtreat\.com|\/collections\/|\/products\//i.test(href + ' ' + cleanText);
      const isLongPathText = /collections\/|products\//i.test(cleanText) || cleanText.length > 36;
      const isNaked =
        looksLikeUrlText ||
        cleanText === href ||
        cleanText.replace(/^https?:\/\//i, '') === href.replace(/^https?:\/\//i, '') ||
        (isStorePath && (looksLikeUrlText || isLongPathText));

      // Always convert collection/product naked links, or any link whose visible text is a URL
      if (!isNaked) {
        // Still convert if the ONLY content of the parent is this collection URL link
        const parentText = ((link.parentElement && link.parentElement.textContent) || '').trim();
        if (!(isStorePath && parentText === rawText)) return;
      }

      if (!href || href === '#') return;
      toCtaButton(link, href);
    });

    // Plain-text URLs (not wrapped in <a>) → button
    const plainUrlPattern = /https?:\/\/[^\s<>"']+/gi;
    content.querySelectorAll('p, li, div').forEach((el) => {
      if (el.classList.contains('article-guide__cta-block')) return;
      if (el.querySelector('a.article-guide__cta-btn, .article-guide__rule')) return;
      if (/^h[1-6]$/i.test(el.tagName)) return;

      const text = (el.textContent || '').trim();
      if (!text) return;

      // Whole element is just a URL (optionally with emoji)
      const onlyUrl = text.replace(/^[👉➡️➜→\s]+/u, '').trim();
      if (/^https?:\/\/\S+$/i.test(onlyUrl) && !el.querySelector('a')) {
        const a = document.createElement('a');
        toCtaButton(a, onlyUrl.replace(/[.,;:!?)]+$/, ''));
        el.innerHTML = '';
        el.appendChild(a);
        el.classList.add('article-guide__cta-btn-wrap');
        return;
      }

      // Mixed content containing a raw URL string → replace URL with button
      if (!el.querySelector('a') && plainUrlPattern.test(el.innerHTML)) {
        plainUrlPattern.lastIndex = 0;
        el.innerHTML = el.innerHTML.replace(plainUrlPattern, (url) => {
          const clean = url.replace(/[.,;:!?)]+$/, '');
          const label = ctaLabelFromHref(clean);
          return (
            '<a class="article-guide__cta-btn button" href="' +
            clean +
            '">' +
            label +
            '</a>'
          );
        });
      }
    });

    wrapCtaBlocks(content);
  }

  function isCtaStart(el) {
    if (!el || el.nodeType !== 1) return false;
    const text = (el.textContent || '').trim();
    if (!text) return false;

    if (/^h[1-6]$/i.test(el.tagName) && /shop\s+.+gift\s+sets|shop\s+easter|browse\s+.+collection/i.test(text)) {
      return true;
    }

    if (/^(P|DIV)$/i.test(el.tagName) && /explore the full .+collection/i.test(text)) {
      return true;
    }

    return false;
  }

  function isProductHeading(el) {
    if (!el || !/^h[1-6]$/i.test(el.tagName)) return false;
    return /^\d+\.\s/.test((el.textContent || '').trim());
  }

  function createRule() {
    const rule = document.createElement('div');
    rule.className = 'article-guide__rule';
    rule.setAttribute('aria-hidden', 'true');
    return rule;
  }

  function wrapCtaBlocks(content) {
    const children = Array.from(content.children);
    const starts = children.filter(isCtaStart);

    starts.forEach((start) => {
      if (start.closest('.article-guide__cta-block')) return;

      const nodes = [start];
      let next = start.nextElementSibling;

      while (next) {
        if (isProductHeading(next) || isCtaStart(next)) break;
        nodes.push(next);

        const hasButton =
          next.classList.contains('article-guide__cta-btn') ||
          next.querySelector?.('.article-guide__cta-btn');

        // Keep collecting through supporting copy + button, then stop
        if (hasButton) {
          next = next.nextElementSibling;
          break;
        }

        next = next.nextElementSibling;

        // Safety: don't swallow long runs of content
        if (nodes.length > 5) break;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'article-guide__cta-block';
      start.parentNode.insertBefore(wrapper, start);
      wrapper.appendChild(createRule());
      nodes.forEach((node) => wrapper.appendChild(node));
      wrapper.appendChild(createRule());
    });
  }

  function init(root) {
    polishContent(root);
    initToc(root);
    initViews(root);
    initReactions(root);
    initCopy(root);
  }

  function boot() {
    document.querySelectorAll('[data-article-guide]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-article-guide]');
    if (root) init(root);
  });
})();
